import { useEffect, useState } from 'react';
import { Stack, Cluster, Text, Badge, Banner, Button, Skeleton, Select } from '../../ui';
import { fetchDeliveryLog, NOTIF_EVENTS, type DeliveryRow } from '../../../lib/notifications-admin';

function statusTone(s: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (s === 'delivered' || s === 'sent') return 'success';
  if (s === 'failed' || s === 'bounced' || s === 'complained') return 'danger';
  if (s === 'undelivered' || s === 'delivery_delayed') return 'warning';
  return 'neutral';
}

function maskRecipient(r: string): string {
  if (r.includes('@')) {
    const [u, d] = r.split('@');
    return `${u.slice(0, 2)}${'*'.repeat(Math.max(1, u.length - 2))}@${d}`;
  }
  return r.length > 5 ? `${r.slice(0, 3)}***${r.slice(-2)}` : r;
}

function when(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function DeliveryLogTab() {
  const [rows, setRows] = useState<DeliveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<string>('all');
  const [event, setEvent] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null);
    fetchDeliveryLog(150)
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load log.'); });
    return () => { alive = false; };
  }, [reloadKey]);

  const filtered = (rows ?? []).filter((r) =>
    (channel === 'all' || r.channel === channel) &&
    (event === 'all' || r.event === event) &&
    (status === 'all' || r.status === status));

  return (
    <Stack gap="group">
      <Cluster gap="tight" justify="between" align="end">
        <Cluster gap="tight" align="center">
          <Select value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Filter by channel" className="w-[120px]">
            <option value="all">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </Select>
          <Select value={event} onChange={(e) => setEvent(e.target.value)} aria-label="Filter by event" className="w-[150px]">
            <option value="all">All events</option>
            {NOTIF_EVENTS.map((ev) => <option key={ev.event} value={ev.event}>{ev.label}</option>)}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status" className="w-[140px]">
            <option value="all">All statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="undelivered">Undelivered</option>
            <option value="bounced">Bounced</option>
          </Select>
        </Cluster>
        <Button size="sm" variant="ghost" onClick={() => setReloadKey((k) => k + 1)}>Refresh</Button>
      </Cluster>

      {error && <Banner tone="danger" title="Couldn't load">{error}</Banner>}

      {!rows ? (
        <Stack gap="tight">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</Stack>
      ) : filtered.length === 0 ? (
        <Banner tone="info" title={rows.length === 0 ? 'No notifications sent yet' : 'No rows match these filters'}>
          {rows.length === 0
            ? 'The pipeline is dormant until Resend and Telnyx are live. Sent notifications will appear here.'
            : 'Try widening the filters above.'}
        </Banner>
      ) : (
        <div className="overflow-hidden rounded-sm border border-border">
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-4 bg-bone px-4 py-2 font-body text-label font-semibold uppercase tracking-[0.04em] text-text-muted">
            <span className="w-[96px]">When</span>
            <span>Recipient</span>
            <span className="w-[110px]">Event</span>
            <span className="w-[90px] text-right">Status</span>
          </div>
          {filtered.map((r) => (
            <div key={r.id} className="border-t border-border px-4 py-2.5">
              <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-4">
                <span className="w-[96px] font-body text-caption text-text-muted">{when(r.created_at)}</span>
                <span className="min-w-0 truncate font-body text-small text-text">
                  <Badge tone={r.channel === 'sms' ? 'ink' : 'neutral'} className="mr-2">{r.channel}</Badge>
                  {maskRecipient(r.recipient)}
                </span>
                <span className="w-[110px] font-body text-caption text-text-muted">{r.event ?? '-'}</span>
                <span className="flex w-[90px] justify-end"><Badge tone={statusTone(r.status)}>{r.status}</Badge></span>
              </div>
              {r.detail && statusTone(r.status) === 'danger' && (
                <Text role="caption" faint as="p" className="mt-1 pl-1">{r.detail}</Text>
              )}
            </div>
          ))}
        </div>
      )}
    </Stack>
  );
}
